import { useCallback, useMemo, useState } from 'react';
import type { FieldSuggestion } from '../../../types/pipelineConfig';
import type {
  ConditionOperator,
  NormalizationCondition,
} from '../../../types/pipelineConfig';
import { FieldPathInput } from '../FieldPathInput';

interface ConditionEditorProps {
  conditions: NormalizationCondition[];
  conditionLogic: 'and' | 'or';
  onConditionsChange: (conditions: NormalizationCondition[]) => void;
  onLogicChange: (logic: 'and' | 'or') => void;
  fieldSuggestions?: FieldSuggestion[];
}

const CONDITION_OPERATORS: ConditionOperator[] = [
  'Equals',
  'NotEquals',
  'Contains',
  'NotContains',
  'RegexMatch',
  'RegexNotMatch',
  'In',
  'NotIn',
  'Exists',
  'NotExists',
];

const NO_VALUE_OPERATORS: ConditionOperator[] = ['Exists', 'NotExists'];
const MULTI_VALUE_OPERATORS: ConditionOperator[] = ['In', 'NotIn'];

function createEmptyCondition(): NormalizationCondition {
  return {
    field: '',
    operator: 'Equals',
    value: '',
  };
}

export function ConditionEditor({
  conditions,
  conditionLogic,
  onConditionsChange,
  onLogicChange,
  fieldSuggestions = [],
}: ConditionEditorProps) {
  const updateCondition = useCallback(
    (index: number, patch: Partial<NormalizationCondition>) => {
      const next = conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, ...patch } : condition,
      );
      onConditionsChange(next);
    },
    [conditions, onConditionsChange],
  );

  const addCondition = useCallback(() => {
    onConditionsChange([...conditions, createEmptyCondition()]);
  }, [conditions, onConditionsChange]);

  const removeCondition = useCallback(
    (index: number) => {
      const next = conditions.filter((_, conditionIndex) => conditionIndex !== index);
      onConditionsChange(next);
    },
    [conditions, onConditionsChange],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Conditions</h4>
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          <button
            type="button"
            onClick={() => onLogicChange('and')}
            className={`px-3 py-1.5 text-xs font-semibold ${
              conditionLogic === 'and'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
            }`}
          >
            AND
          </button>
          <button
            type="button"
            onClick={() => onLogicChange('or')}
            className={`px-3 py-1.5 text-xs font-semibold ${
              conditionLogic === 'or'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
            }`}
          >
            OR
          </button>
        </div>
      </div>

      {conditions.length === 0 && (
        <p className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          No conditions defined. This conditional will match all events.
        </p>
      )}

      {conditions.map((condition, index) => (
        <div
          key={index}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/40 p-3 space-y-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
            <div className="md:col-span-5">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Field
              </label>
              <FieldPathInput
                value={condition.field}
                onChange={(value) => updateCondition(index, { field: value })}
                suggestions={fieldSuggestions}
                placeholder="e.g., data.essentials.severity"
              />
            </div>

            <div className="md:col-span-3">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                Operator
              </label>
              <select
                className="input"
                value={condition.operator}
                onChange={(event) => {
                  const operator = event.target.value as ConditionOperator;
                  if (NO_VALUE_OPERATORS.includes(operator)) {
                    updateCondition(index, { operator, value: undefined, values: undefined });
                    return;
                  }
                  if (MULTI_VALUE_OPERATORS.includes(operator)) {
                    updateCondition(index, { operator, value: undefined, values: condition.values ?? [] });
                    return;
                  }
                  updateCondition(index, { operator, value: condition.value ?? '', values: undefined });
                }}
              >
                {CONDITION_OPERATORS.map((operator) => (
                  <option key={operator} value={operator}>
                    {operator}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              {!NO_VALUE_OPERATORS.includes(condition.operator) && (
                <>
                  <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                    Value
                  </label>
                  {MULTI_VALUE_OPERATORS.includes(condition.operator) ? (
                    <ChipValueEditor
                      values={condition.values ?? []}
                      onChange={(values) => updateCondition(index, { values, value: undefined })}
                    />
                  ) : (
                    <input
                      type="text"
                      className="input"
                      value={condition.value ?? ''}
                      onChange={(event) => updateCondition(index, { value: event.target.value, values: undefined })}
                      placeholder="value"
                    />
                  )}
                </>
              )}
            </div>

            <div className="md:col-span-1 flex md:justify-end">
              <button
                type="button"
                onClick={() => removeCondition(index)}
                className="mt-6 md:mt-0 inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                title="Remove condition"
                aria-label="Remove condition"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addCondition}
        className="w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-400"
      >
        + Add Condition
      </button>
    </div>
  );
}

function ChipValueEditor({
  values,
  onChange,
}: {
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const [draftValue, setDraftValue] = useState('');

  const uniqueValues = useMemo(() => values.filter((value, index) => values.indexOf(value) === index), [values]);

  const addValue = useCallback(() => {
    const normalized = draftValue.trim();
    if (!normalized) return;
    if (uniqueValues.includes(normalized)) {
      setDraftValue('');
      return;
    }
    onChange([...uniqueValues, normalized]);
    setDraftValue('');
  }, [draftValue, onChange, uniqueValues]);

  return (
    <div className="space-y-2">
      {uniqueValues.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {uniqueValues.map((value, index) => (
            <span
              key={`${value}-${index}`}
              className="inline-flex items-center gap-1 rounded-full bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 text-xs text-primary-800 dark:text-primary-200"
            >
              {value}
              <button
                type="button"
                onClick={() => onChange(uniqueValues.filter((chipValue) => chipValue !== value))}
                className="text-primary-700 dark:text-primary-200 hover:text-red-600"
                aria-label={`Remove ${value}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          className="input"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addValue();
            }
          }}
          placeholder="Add value"
        />
        <button type="button" onClick={addValue} className="btn-secondary text-xs whitespace-nowrap">
          Add
        </button>
      </div>
    </div>
  );
}

export type { ConditionEditorProps };
